<!--
  Thân màn CHI TIẾT hoa hồng một nhân viên — SRS §7.7.
  Tách riêng khỏi ReportDetail.vue để phần hiển thị gọn và dùng lại được.
  Dữ liệu tổng quan / cộng-trừ / nhóm nhận qua props; riêng danh sách hoá đơn
  tự gọi API có phân trang (§7.5 #3) vì một nhân viên có thể hơn 1.200 dòng.
-->
<template>
  <div>
        <!-- Đang tải -->
        <div v-if="onLoading">
          <vcl-table :speed="5" :animate="true" :columns="8"></vcl-table>
        </div>

        <!-- Rỗng / lỗi -->
        <div v-else-if="!overview" class="text-center text-muted py-10">
          Không có dữ liệu hoa hồng của nhân viên trong kỳ đã chọn.
        </div>

        <div v-else>
          <!-- 1. Tổng quan (§7.7): nhân viên + kỳ, tổng thưởng và các khoản cấu thành -->
          <div class="detail-hero mb-4">
            <div>
              <div class="detail-hero__name">
                {{ overview.employeeName || '--' }}
              </div>
              <div class="detail-hero__meta">
                <span v-if="overview.employeeCode">{{ overview.employeeCode }}</span>
                <span>{{ overview.jobTitleName || '--' }}</span>
              </div>
            </div>
            <div class="detail-hero__side">
              <div class="detail-hero__period">{{ displayPeriod(period) }}</div>
              <div class="detail-hero__meta">
                <span>{{ scopeLabel }}: {{ scopeName || '--' }}</span>
              </div>
            </div>
          </div>

          <div class="detail-summary mb-6">
            <div class="detail-summary__total">
              <div class="detail-summary__label">Tổng thưởng</div>
              <div
                class="detail-summary__total-value"
                :class="amountClass(overview.totalAmount)"
              >
                {{ displayAmount(overview.totalAmount) }}
              </div>
            </div>
            <div class="detail-summary__parts">
              <div
                v-for="part in summaryParts"
                :key="part.key"
                class="detail-summary__part"
              >
                <div class="detail-summary__label">
                  <span class="detail-summary__dot" :class="part.dot"></span>
                  {{ part.label }}
                </div>
                <div class="detail-summary__part-value" :class="amountClass(part.raw)">
                  {{ part.value }}
                </div>
              </div>
            </div>
          </div>

          <b-row>
            <!-- 2. Chi tiết theo nhóm hoa hồng -->
            <b-col cols="12" class="mb-6">
              <div class="detail-section">
                Theo nhóm hoa hồng
                <span class="detail-section__count">{{ categoryGroups.length }}</span>
              </div>
              <div v-if="!categoryGroups.length" class="detail-empty">
                Không có nhóm hoa hồng nào phát sinh trong kỳ
              </div>
              <div
                v-for="group in categoryGroups"
                :key="group.commissionCategoryId"
                class="category-group mb-2"
              >
                <div
                  class="category-group__head"
                  @click="toggleGroup(group.commissionCategoryId)"
                >
                  <i
                    class="fas category-group__caret"
                    :class="
                      isGroupOpen(group.commissionCategoryId)
                        ? 'fa-chevron-down'
                        : 'fa-chevron-right'
                    "
                  ></i>
                  <span class="category-group__name">{{
                    group.commissionCategoryName || 'Không xác định'
                  }}</span>
                  <span class="category-group__qty"
                    >SL {{ displayQuantity(group.quantity) }}</span
                  >
                  <span
                    class="category-group__amount"
                    :class="amountClass(group.amount)"
                    >{{ displayAmount(group.amount) }}</span
                  >
                </div>
                <b-collapse :visible="isGroupOpen(group.commissionCategoryId)">
                  <b-table
                    :items="group.details || []"
                    :fields="categoryDetailFields"
                    small
                    borderless
                    show-empty
                    empty-text="Không có mức thưởng nào khớp"
                    class="mb-0"
                  >
                    <template v-slot:cell(commissionProgramName)="row">
                      {{ row.item.commissionProgramName }}
                      <span
                        v-if="row.item.adhocRewardModeName"
                        class="label label-inline font-weight-bold ml-1"
                        :class="adhocModeVariant(row.item.adhocRewardMode)"
                        >{{ row.item.adhocRewardModeName }}</span
                      >
                    </template>
                    <template v-slot:cell(rate)="row">
                      <span>{{ displayRate(row.item) }}</span>
                    </template>
                    <template v-slot:cell(quantity)="row">
                      <span>{{ displayQuantity(row.item.quantity) }}</span>
                    </template>
                    <template v-slot:cell(amount)="row">
                      <span class="font-weight-bold" :class="amountClass(row.item.amount)">{{
                        displayAmount(row.item.amount)
                      }}</span>
                    </template>
                  </b-table>
                </b-collapse>
              </div>
            </b-col>

            <!-- 3. Khoản cộng / trừ -->
            <b-col cols="12" class="mb-6">
              <div class="detail-section">
                Cộng / trừ khác
                <span class="detail-section__count">{{ adjustments.length }}</span>
              </div>
              <div v-if="!adjustments.length" class="detail-empty">
                Không có khoản cộng / trừ nào trong kỳ
              </div>
              <div v-else class="detail-card">
                <div
                  v-for="(item, index) in adjustments"
                  :key="'adj-' + index"
                  class="adjust-row"
                >
                  <div class="adjust-row__main">
                    <div class="adjust-row__title">
                      {{ adjustmentReasonName(item) }}
                      <span
                        class="label label-inline font-weight-bold ml-1"
                        :class="sourceTypeVariant(item.sourceType)"
                        >{{ adjustmentSourceName(item) }}</span
                      >
                    </div>
                    <div class="adjust-row__meta">
                      {{ displayDate(item.date) }}
                      <template v-if="item.billNumber"> · HĐ {{ item.billNumber }}</template>
                      <template v-if="item.description"> · {{ item.description }}</template>
                    </div>
                  </div>
                  <div class="adjust-row__value" :class="adjustmentOperatorClass(item)">
                    {{ operatorSign(item) }}{{ adjustmentValue(item) }}
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- 4. Danh sách hóa đơn — gọi API riêng, phân trang + tìm kiếm (§7.5 #3) -->
          <div class="detail-section">
            Hóa đơn
            <span class="detail-section__count">{{ billTotalRow }}</span>
          </div>
          <div class="detail-filter mb-3">
            <b-row>
              <b-col md="2">
                <b-input
                  v-model="billFilter.billNumber"
                  size="sm"
                  placeholder="Mã hóa đơn"
                  autocomplete="off"
                  @keyup.enter="onBillSearch"
                ></b-input>
              </b-col>
              <b-col md="3">
                <b-input
                  v-model="billFilter.productName"
                  size="sm"
                  placeholder="Tên sản phẩm"
                  autocomplete="off"
                  @keyup.enter="onBillSearch"
                ></b-input>
              </b-col>
              <b-col md="3">
                <b-input
                  v-model="billFilter.commissionCategoryName"
                  size="sm"
                  placeholder="Nhóm hoa hồng"
                  autocomplete="off"
                  @keyup.enter="onBillSearch"
                ></b-input>
              </b-col>
              <b-col md="2">
                <b-form-select
                  v-model="billFilter.sourceType"
                  size="sm"
                  :options="sourceTypeOptions"
                  value-field="id"
                  text-field="name"
                >
                  <template v-slot:first>
                    <b-form-select-option :value="null"
                      >-- Nguồn --</b-form-select-option
                    >
                  </template>
                </b-form-select>
              </b-col>
              <b-col md="2">
                <b-button
                  size="sm"
                  variant="primary"
                  class="px-5 mr-1"
                  @click="onBillSearch"
                  >Tìm</b-button
                >
                <b-button size="sm" variant="light" @click="resetBillFilter"
                  >Xóa</b-button
                >
              </b-col>
            </b-row>
          </div>
          <b-table
            :items="billRows"
            :busy="billLoading"
            :fields="billFields"
            bordered
            hover
            show-empty
            empty-text="Không có hóa đơn nào phát sinh hoa hồng trong kỳ"
            :tbody-tr-class="billRowClass"
          >
            <template v-slot:table-busy>
              <vcl-table :speed="5" :animate="true" :columns="6"></vcl-table>
            </template>
            <template v-slot:cell(billNumber)="row">
              <div>{{ row.item.billNumber || '--' }}</div>
              <div class="text-muted small">
                {{ displayDate(row.item.billDate) }}
              </div>
              <div
                v-if="row.item.relatedBillNumber"
                class="text-muted small"
              >
                HĐ gốc: {{ row.item.relatedBillNumber }}
              </div>
            </template>
            <template v-slot:cell(productName)="row">
              <div class="bill-product">{{ row.item.productName || '--' }}</div>
              <div class="small mt-1">
                <span
                  class="label label-inline font-weight-bold mr-1"
                  :class="sourceTypeVariant(row.item.sourceType)"
                  >{{ sourceTypeName(row.item) }}</span
                >
                <span
                  v-if="row.item.adhocRewardModeName"
                  class="label label-inline font-weight-bold mr-1"
                  :class="adhocModeVariant(row.item.adhocRewardMode)"
                  >{{ row.item.adhocRewardModeName }}</span
                >
                <span class="text-muted">{{ row.item.jobTitleName }}</span>
              </div>
              <div
                v-if="Number(row.item.adhocReplacedAmount) > 0"
                class="text-muted small mt-1"
              >
                Thay hoa hồng thường
                {{ displayAmount(row.item.adhocReplacedAmount) }}
              </div>
            </template>
            <template v-slot:cell(quantity)="row">
              <span>{{ displayQuantity(row.item.quantity) }}</span>
            </template>
            <template v-slot:cell(revenue)="row">
              <span>{{ displayAmount(row.item.revenue) }}</span>
            </template>
            <template v-slot:cell(commissionCategoryName)="row">
              <div>{{ row.item.commissionCategoryName || '--' }}</div>
              <div class="text-muted small">
                {{ row.item.commissionProgramName || '--' }} ·
                {{ displayRate(row.item) }}
              </div>
            </template>
            <template v-slot:cell(amount)="row">
              <span
                class="font-weight-bold"
                :class="amountClass(signedAmount(row.item))"
                >{{ displayAmount(signedAmount(row.item)) }}</span
              >
            </template>

            <!-- Tổng cộng hoa hồng từ hóa đơn -->
            <template v-slot:custom-foot>
              <tr class="report-table__foot" v-if="billRows.length">
                <td colspan="2" class="text-right font-weight-bold">
                  Tổng cộng
                </td>
                <td class="text-center font-weight-bold">
                  {{ displayQuantity(billTotals.quantity) }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ displayAmount(billTotals.revenue) }}
                </td>
                <td></td>
                <td
                  class="text-right font-weight-bold"
                  :class="amountClass(billTotals.amount)"
                >
                  {{ displayAmount(billTotals.amount) }}
                </td>
              </tr>
            </template>
          </b-table>

          <b-row v-if="billTotalPage > 1">
            <b-col>
              <p class="mt-3 text-muted">
                Trang {{ billPage }}/{{ billTotalPage }}
              </p>
            </b-col>
            <b-col>
              <b-pagination
                v-model="billPage"
                :total-rows="billTotalRow"
                :per-page="BILL_PAGE_LIMIT"
                align="right"
                size="sm"
                class="mt-3"
                @change="onBillPageChange"
              ></b-pagination>
            </b-col>
          </b-row>
        </div>
  </div>
</template>

<script>
import { VclTable } from 'vue-content-loading';
import ApiService from '@/core/services/api.service';
import { convertPrice, makeToastFaile } from '@/utils/common';

const BILL_PAGE_LIMIT = 20;
import {
  COMMISSION_REPORT_TYPE_NAME,
  COMMISSION_ADJUSTMENT_OPERATOR,
  COMMISSION_ADJUSTMENT_AMOUNT_TYPE,
  COMMISSION_ADJUSTMENT_AMOUNT_TYPE_SIGN,
  COMMISSION_ADJUSTMENT_REASON_NAME,
  COMMISSION_SOURCE_TYPE_NAME,
  COMMISSION_ADHOC_REWARD_MODE,
  toOptionList,
} from './commission.constants';
import commissionReportMixin, { thStyle } from './commissionReport.mixin';

export default {
  components: {
    VclTable,
  },
  mixins: [commissionReportMixin],
  props: {
    onLoading: { type: Boolean, default: false },
    overview: { type: Object, default: null },
    period: { type: Object, default: null },
    store: { type: Object, default: null },
    departmentName: { type: String, default: '' },
    adjustments: { type: Array, default: () => [] },
    categoryGroups: { type: Array, default: () => [] },
    /** Tham số để tự gọi API danh sách hoá đơn (§7.5 #3) */
    billQuery: { type: Object, default: null },
  },
  data() {
    return {
      BILL_PAGE_LIMIT,
      billRows: [],
      billLoading: false,
      sourceTypeOptions: toOptionList(COMMISSION_SOURCE_TYPE_NAME),
      billFilter: {
        billNumber: '',
        productName: '',
        commissionCategoryName: '',
        sourceType: null,
      },
      billPage: 1,
      billTotalRow: 0,
      billTotalPage: 1,
      billApiTotals: { quantity: 0, revenue: 0, amount: 0 },

      openGroupIds: [],
      categoryDetailFields: [
        {
          key: 'commissionProgramName',
          label: 'Chương trình',
          thStyle: thStyle('45%'),
        },
        {
          key: 'rate',
          label: 'Mức thưởng',
          thStyle: thStyle('20%'),
          tdClass: 'text-center',
        },
        {
          key: 'quantity',
          label: 'Số lượng',
          thStyle: thStyle('15%'),
          tdClass: 'text-center',
        },
        {
          key: 'amount',
          label: 'Thành tiền',
          thStyle: thStyle('20%'),
          tdClass: 'text-right',
        },
      ],
      billFields: [
        {
          key: 'billNumber',
          label: 'Hóa đơn',
          thStyle: thStyle('12%'),
        },
        {
          key: 'productName',
          label: 'Sản phẩm',
          thStyle: thStyle('32%'),
        },
        {
          key: 'quantity',
          label: 'SL',
          thStyle: thStyle('6%'),
          tdClass: 'text-center',
        },
        {
          key: 'revenue',
          label: 'Doanh thu',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        {
          key: 'commissionCategoryName',
          label: 'Nhóm / Chương trình',
          thStyle: thStyle('26%'),
        },
        {
          key: 'amount',
          label: 'Thành tiền',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
      ],
    };
  },
  computed: {
    pageTitle() {
      const base =
        COMMISSION_REPORT_TYPE_NAME[this.reportType] || 'Báo cáo hoa hồng';
      return `Chi tiết hoa hồng nhân viên — ${base}`;
    },
    employeeLabel() {
      if (!this.overview) {
        return '';
      }
      return this.overview.employeeCode
        ? `${this.overview.employeeName} (${this.overview.employeeCode})`
        : this.overview.employeeName || '--';
    },
    scopeName() {
      return this.displayScopeName({
        store: this.store,
        departmentName: this.departmentName,
      });
    },
    /** Bốn khoản cấu thành Tổng thưởng (§7.4) — overview đã cộng dấu sẵn */
    summaryParts() {
      const overview = this.overview || {};
      return [
        { key: 'commissionAmount', label: 'Hoa hồng', dot: 'is-commission' },
        { key: 'adhocAmount', label: 'AdHoc', dot: 'is-adhoc' },
        { key: 'bonusAmount', label: 'Cộng khác', dot: 'is-bonus' },
        { key: 'minusAmount', label: 'Trừ khác', dot: 'is-minus' },
      ].map((part) => ({
        ...part,
        raw: overview[part.key],
        value: this.displayAmount(overview[part.key]),
      }));
    },
    /** Tổng cuối bảng hóa đơn — amount phải nhân direction trước khi cộng (§7.4) */
    /**
     * Tổng của TOÀN BỘ kết quả lọc (API trả về), KHÔNG phải tổng trang đang xem —
     * nếu cộng theo trang thì đứng ở trang 1 sẽ thấy tổng sai.
     */
    billTotals() {
      return this.billApiTotals;
    },
  },
  watch: {
    // Đổi nhân viên / đổi kỳ -> nạp lại danh sách hoá đơn từ đầu
    billQuery: {
      handler() {
        this.resetBillFilterState();
        this.billPage = 1;
        this.fetchBills();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    /* ------------------------ Danh sách hoá đơn (§7.5 #3) ------------------ */
    /** Bấm Tìm (hoặc Enter trong ô nhập) mới gọi API — không tự tìm khi đang gõ */
    onBillSearch() {
      this.billPage = 1;
      this.fetchBills();
    },
    resetBillFilterState() {
      this.billFilter = {
        billNumber: '',
        productName: '',
        commissionCategoryName: '',
        sourceType: null,
      };
    },
    resetBillFilter() {
      this.resetBillFilterState();
      this.billPage = 1;
      this.fetchBills();
    },
    onBillPageChange(page) {
      this.billPage = page;
      this.$nextTick(() => this.fetchBills());
    },
    /** Gọi API riêng cho danh sách hoá đơn — không nhét hết vào /details */
    fetchBills() {
      if (!this.billQuery || !this.billQuery.employeeId) {
        this.billRows = [];
        this.billTotalRow = 0;
        this.billTotalPage = 1;
        this.billApiTotals = { quantity: 0, revenue: 0, amount: 0 };
        return;
      }
      this.billLoading = true;
      const params = {
        ...this.billQuery,
        page: this.billPage,
        limit: BILL_PAGE_LIMIT,
      };
      // Mỗi tiêu chí gửi một trường riêng — BE so đúng cột đó, không quét OR
      Object.keys(this.billFilter).forEach((key) => {
        const value = this.billFilter[key];
        if (value !== null && value !== '') {
          params[key] = value;
        }
      });
      ApiService.setHeader();
      ApiService.query('commission-reports/bills', { params })
        .then(({ data }) => {
          const result = data.data || {};
          this.billRows = result.data || [];
          this.billTotalRow = result.totalRow || 0;
          this.billTotalPage = result.totalPage || 1;
          const totals = result.totals || {};
          this.billApiTotals = {
            quantity: totals.quantity || 0,
            revenue: totals.revenue || 0,
            amount: totals.amount || 0,
          };
        })
        .catch((error) => {
          this.billRows = [];
          this.billTotalRow = 0;
          this.billTotalPage = 1;
          const message =
            error.response && error.response.data
              ? error.response.data.message
              : 'Không tải được danh sách hóa đơn!';
          makeToastFaile(message);
        })
        .finally(() => {
          this.billLoading = false;
        });
    },

    backQuery() {
      return { type: this.reportType };
    },

    /** Nhãn AdHoc: ghi đè hoa hồng thường (vàng) hay cộng thêm (xanh) */
    adhocModeVariant(mode) {
      return Number(mode) === COMMISSION_ADHOC_REWARD_MODE.PLUS
        ? 'label-light-info'
        : 'label-light-warning';
    },

    /* --------------------------- Nhóm hoa hồng --------------------------- */
    isGroupOpen(categoryId) {
      return this.openGroupIds.indexOf(categoryId) > -1;
    },
    toggleGroup(categoryId) {
      const index = this.openGroupIds.indexOf(categoryId);
      if (index > -1) {
        this.openGroupIds.splice(index, 1);
      } else {
        this.openGroupIds.push(categoryId);
      }
    },
    /** Mức thưởng cấu hình: tiền hay % (dùng chung cho nhóm và bảng hóa đơn) */
    displayRate(item) {
      if (!item || item.rateValue === null || item.rateValue === undefined) {
        return '--';
      }
      const value = Number(item.rateValue) || 0;
      return Number(item.rateType) === COMMISSION_ADJUSTMENT_AMOUNT_TYPE.PERCENT
        ? `${convertPrice(value)}%`
        : this.displayAmount(value);
    },

    /* ---------------------------- Khoản cộng/trừ ------------------------- */
    adjustmentSourceName(item) {
      return (
        item.sourceTypeName ||
        COMMISSION_SOURCE_TYPE_NAME[item.sourceType] ||
        '--'
      );
    },
    adjustmentReasonName(item) {
      return (
        item.reasonName ||
        COMMISSION_ADJUSTMENT_REASON_NAME[item.reasonType] ||
        '--'
      );
    },
    adjustmentOperatorClass(item) {
      return Number(item.operatorType) === COMMISSION_ADJUSTMENT_OPERATOR.MINUS
        ? 'text-danger font-weight-bold'
        : 'text-success font-weight-bold';
    },
    /**
     * Giá trị bút toán: hiện giá trị cấu hình (tiền / %) và số tiền quy đổi
     * khi cấu hình là phần trăm.
     */
    adjustmentValue(item) {
      const amountType = Number(item.amountType);
      const sign =
        COMMISSION_ADJUSTMENT_AMOUNT_TYPE_SIGN[amountType] ||
        COMMISSION_ADJUSTMENT_AMOUNT_TYPE_SIGN[
          COMMISSION_ADJUSTMENT_AMOUNT_TYPE.AMOUNT
        ];
      const amount = this.displayAmount(item.amount);
      if (amountType === COMMISSION_ADJUSTMENT_AMOUNT_TYPE.PERCENT) {
        return `${convertPrice(Number(item.rateValue) || 0)}${sign} = ${amount}`;
      }
      return amount;
    },
    operatorSign(item) {
      return Number(item.operatorType) === COMMISSION_ADJUSTMENT_OPERATOR.MINUS
        ? '−'
        : '+';
    },

    /* ------------------------------ Hóa đơn ------------------------------ */
    billRowClass(item) {
      return item && this.isMinusRow(item) ? 'table-danger-soft' : '';
    },

    /* ---------------------------- Tải dữ liệu ---------------------------- */
  },
};
</script>

<style scoped>
.detail-hero {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
}
.detail-hero__name {
  font-size: 1.35rem;
  font-weight: 700;
  color: #181c32;
}
.detail-hero__period {
  font-size: 1rem;
  font-weight: 600;
  color: #181c32;
}
.detail-hero__side {
  text-align: right;
}
.detail-hero__meta {
  font-size: 0.9rem;
  color: #7e8299;
}
.detail-hero__meta span + span::before {
  content: '·';
  margin: 0 6px;
}

.detail-summary {
  display: flex;
  flex-wrap: wrap;
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  overflow: hidden;
}
.detail-summary__total {
  flex: 0 0 260px;
  padding: 1.1rem 1.5rem;
  background-color: #f1faff;
  border-right: 1px solid #ecf0f3;
}
.detail-summary__total-value {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.2;
  color: #187de4;
}
.detail-summary__parts {
  flex: 1 1 480px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}
.detail-summary__part {
  padding: 1.1rem 1.25rem;
  border-right: 1px solid #ecf0f3;
}
.detail-summary__part:last-child {
  border-right: 0;
}
.detail-summary__label {
  font-size: 0.85rem;
  color: #7e8299;
  margin-bottom: 0.3rem;
}
.detail-summary__part-value {
  font-size: 1.15rem;
  font-weight: 600;
  color: #181c32;
}
.detail-summary__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.35rem;
}
.detail-summary__dot.is-commission {
  background-color: #3699ff;
}
.detail-summary__dot.is-adhoc {
  background-color: #8950fc;
}
.detail-summary__dot.is-bonus {
  background-color: #1bc5bd;
}
.detail-summary__dot.is-minus {
  background-color: #f64e60;
}

.detail-section {
  font-size: 13px;
  font-weight: 600;
  color: #181c32;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 12px;
}
.detail-section__count {
  display: inline-block;
  min-width: 20px;
  padding: 0 6px;
  margin-left: 4px;
  border-radius: 10px;
  background: #ecf0f3;
  color: #7e8299;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
.detail-empty {
  border: 1px dashed #d6d9e0;
  border-radius: 0.42rem;
  padding: 16px;
  color: #7e8299;
  font-size: 13px;
  text-align: center;
}
.detail-card {
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  overflow: hidden;
}
.detail-filter {
  background: #f8f9fb;
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  padding: 12px 16px;
}

/* Khối nhóm hoa hồng: đầu nhóm bấm để bung/thu */
.category-group {
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  overflow: hidden;
}
.category-group__head {
  display: flex;
  align-items: center;
  padding: 0.7rem 1rem;
  cursor: pointer;
}
.category-group__head:hover {
  background-color: #f8f9fb;
}
.category-group__caret {
  width: 18px;
  font-size: 0.75rem;
  color: #b5b5c3;
}
.category-group__name {
  flex: 1 1 auto;
  font-weight: 600;
  color: #181c32;
}
.category-group__qty {
  color: #7e8299;
  font-size: 0.85rem;
  margin-right: 1.25rem;
}
.category-group__amount {
  min-width: 90px;
  text-align: right;
  font-weight: 700;
}

.adjust-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-top: 1px solid #ecf0f3;
}
.adjust-row:first-child {
  border-top: 0;
}
.adjust-row__title {
  font-weight: 600;
  color: #181c32;
}
.adjust-row__meta {
  font-size: 12px;
  color: #7e8299;
  margin-top: 2px;
}
.adjust-row__value {
  white-space: nowrap;
}

.bill-product {
  font-weight: 600;
  color: #181c32;
}

.report-table__foot > td {
  background-color: #f3f6f9;
}

.table >>> tr.table-danger-soft > td {
  background-color: #fff5f5;
  color: #d33;
}
</style>
